import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import {
  seatWorkspaceApi,
  type SeatPerspective,
  type SeatProjection,
} from "@/lib/api/seatWorkspace";

export const seatWorkspaceQueryKey = (
  tenantId: string | null | undefined,
  userId: string | null | undefined,
  perspective: SeatPerspective | null,
) =>
  [
    "seat-workspace",
    tenantId ?? "unresolved",
    userId ?? "unresolved",
    perspective ?? "unresolved",
  ] as const;

/**
 * One authenticated read per seat, keyed by tenant, user, and perspective.
 *
 * ONLY a 404 is a settled refusal — the endpoint answers `404 "Workspace
 * unavailable"` for every seat the caller does not hold — so only a 404 is
 * latched against the identity that earned it, with the cached projection
 * dropped. The latch clears the moment any of the three identities changes.
 *
 * A 401 and a 403 are NOT that fact. A 401 is a credential that expired or is
 * mid-refresh and a 403 is a membership that is inactive or not yet resolved;
 * both recover on their own, and neither is evidence that the seat does not
 * exist. Latching them replaced a live dashboard with missing-record copy that
 * a recovered credential could no longer clear, because the latch survives
 * every refetch until the page is remounted. They stay ordinary errors — still
 * never retried, since a 4xx is not a transport failure — so the caller renders
 * its error surface and its retry.
 *
 * `perspective` is nullable so a page that resolves its seat from a role can
 * call the hook unconditionally and stay disabled when there is no seat to read.
 */
export function useSeatWorkspace(perspective: SeatPerspective | null) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = me?.tenant_id;
  const userId = me?.user_id;
  const queryKey = seatWorkspaceQueryKey(tenantId, userId, perspective);
  const identity = queryKey.join(":");
  const [refusedIdentity, setRefusedIdentity] = useState<string | null>(null);
  const refused = refusedIdentity === identity;

  useEffect(() => {
    if (refused) queryClient.removeQueries({ queryKey, exact: true });
  }, [identity, queryClient, queryKey, refused]);

  const query = useQuery({
    queryKey,
    enabled: Boolean(tenantId && userId && perspective) && !refused,
    // An act this app made (a roll, a publication, a judgement, a recording's
    // notice) drops this read through `refreshHomeAfterAct`, so Home never
    // paints the projection from before it. Every mount still reads the seat
    // again: another person's act, or one not routed through the helper,
    // would otherwise hold until the global `staleTime` ran out.
    refetchOnMount: "always",
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
    queryFn: async (): Promise<SeatProjection> => {
      try {
        const projection = perspective === "teaching"
          ? await seatWorkspaceApi.getTeaching()
          : await seatWorkspaceApi.getLearning();
        setRefusedIdentity((previous) => previous === identity ? null : previous);
        return projection;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          setRefusedIdentity(identity);
        }
        throw error;
      }
    },
  });
  return {
    ...query,
    data: refused ? undefined : query.data,
    isPending: refused ? false : query.isPending,
    isNoSeat: refused,
  };
}
